import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Permissions } from '../services/permissionsService';
import { 
  fetchPermissions, 
  selectPermissions, 
  hasPermission as checkPermission, 
  selectPermissionsInitialized 
} from '../store/slices/permissionsSlice';

// إنشاء السياق (Context)
const PermissionsContext = createContext(null);

/**
 * مزود سياق الصلاحيات
 * يوفر الوصول إلى دوال التحقق من الصلاحيات في أي مكان في التطبيق
 * تحديث: يستخدم الآن Redux لإدارة الصلاحيات
 * @param {Object} props خصائص المكون
 * @returns {JSX.Element} مكون مزود السياق
 */
export function PermissionsProvider({ children }) {
  // استخدام Redux للصلاحيات
  const dispatch = useDispatch();
  const permissions = useSelector(selectPermissions);
  const initialized = useSelector(selectPermissionsInitialized);
  const [currentPermissions, setCurrentPermissions] = useState([]);

  // تحميل الصلاحيات عند بدء التطبيق أو تغيير حالة Redux
  useEffect(() => {
    // التأكد من تهيئة المخزن
    if (!initialized) {
      dispatch(fetchPermissions());
    }
    
    // استخدام الصلاحيات من Redux
    setCurrentPermissions(permissions);
    
    // الاستماع لتغييرات الصلاحيات من مصادر أخرى (للتوافق مع الأنظمة القديمة)
    const handleStorageEvent = () => {
      
      // بدلاً من طلب API جديد، نتحقق من التخزين المحلي
      try {
        // التحقق من localStorage للتوافق
        const storedPermissions = localStorage.getItem('rolePermissions');
        if (storedPermissions) {
          const parsedPermissions = JSON.parse(storedPermissions);
          dispatch({ 
            type: 'permissions/fetchPermissions/fulfilled', 
            payload: parsedPermissions 
          });
        }
      } catch (error) {
        console.error('خطأ في تحليل الصلاحيات المخزنة:', error);
      }
    };
    
    // الاستماع لتغييرات التخزين المحلي
    window.addEventListener('storage', handleStorageEvent);
    
    // إزالة المستمع عند تفكيك المكون
    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [permissions, initialized, dispatch]);
  
  // تعريف دوال التحقق من الصلاحيات التي تستخدم المخزن
  const hasPermission = (permission) => {
    // استخدام التحقق مباشرة من المكون الحالي - أسرع من التحقق من Redux Store
    return Array.isArray(currentPermissions) && currentPermissions.includes(permission);
  };
  
  const getUserPermissions = () => {
    return currentPermissions;
  };
  
  // القيمة التي سيتم توفيرها للمكونات
  const value = {
    // دوال التحقق من الصلاحيات
    hasPermission,
    getUserPermissions,
    currentPermissions,
    Permissions,
    
    // دوال مساعدة شائعة الاستخدام
    canViewCourse: () => hasPermission('view_courses'),
    canEditCourse: () => hasPermission('edit_course'),
    canViewCourseContent: () => hasPermission('view_course_content'),
    canAccessAdminDashboard: () => hasPermission('access_dashboard'),
    
    // إضافة بعض الوظائف المطورة من Redux
    refreshPermissions: () => dispatch(fetchPermissions())
  };
  
  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

/**
 * هوك (Hook) للوصول إلى سياق الصلاحيات
 * @returns {Object} كائن يحتوي على دوال التحقق من الصلاحيات
 */
export function usePermissions() {
  const context = useContext(PermissionsContext);
  
  if (context === null) {
    throw new Error(
      'usePermissions يجب استخدامه داخل PermissionsProvider'
    );
  }
  
  return context;
}

export default PermissionsProvider;
